import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";
import { DATASET_YEARS } from "../utils/cmsApi";
import { useUser } from "@clerk/react";
import { getWatchedProviders, unwatchProvider, getWatchedCompanies, unwatchCompany } from "../utils/alertsApi";
import CompareButton from "../components/CompareButton";
import CompanyCompareButton from "../components/CompanyCompareButton";

const US_STATES = [
  "ALL","AK","AL","AR","AZ","CA","CO","CT","DC","DE","FL","GA","HI",
  "IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO",
  "MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR",
  "PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY",
];

const SPECIALTIES = [
  "ALL",
  "Allopathic & Osteopathic Physicians|Anesthesiology",
  "Allopathic & Osteopathic Physicians|Cardiology",
  "Allopathic & Osteopathic Physicians|Dermatology",
  "Allopathic & Osteopathic Physicians|Emergency Medicine",
  "Allopathic & Osteopathic Physicians|Family Medicine",
  "Allopathic & Osteopathic Physicians|Gastroenterology",
  "Allopathic & Osteopathic Physicians|General Surgery",
  "Allopathic & Osteopathic Physicians|Internal Medicine",
  "Allopathic & Osteopathic Physicians|Neurology",
  "Allopathic & Osteopathic Physicians|Obstetrics & Gynecology",
  "Allopathic & Osteopathic Physicians|Oncology",
  "Allopathic & Osteopathic Physicians|Ophthalmology",
  "Allopathic & Osteopathic Physicians|Orthopedic Surgery",
  "Allopathic & Osteopathic Physicians|Pathology",
  "Allopathic & Osteopathic Physicians|Pediatrics",
  "Allopathic & Osteopathic Physicians|Psychiatry & Neurology",
  "Allopathic & Osteopathic Physicians|Pulmonology",
  "Allopathic & Osteopathic Physicians|Radiology",
  "Allopathic & Osteopathic Physicians|Rheumatology",
  "Allopathic & Osteopathic Physicians|Urology",
];

function specialtyLabel(s) {
  if (s === "ALL") return "All specialties";
  return s.includes("|") ? s.split("|")[1] : s;
}

import { KNOWN_COMPANIES, titleCaseCompany } from "../utils/companies";

export default function HomePage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [npi, setNpi] = useState("");
  const [state, setState] = useState("ALL");
  const [specialty, setSpecialty] = useState("ALL");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  const [companyInput, setCompanyInput] = useState("");
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);  
  const navigate = useNavigate();
  const { canSearch, incrementSearch, setShowUpgradeModal, setShowSignInModal, setUpgradeMessage, isPro, searchesRemaining } = useSubscription();  
  const { user, isSignedIn } = useUser();
  const [watchedProviders, setWatchedProviders] = useState([]);
  const [watchListOpen, setWatchListOpen] = useState(false);
  const [watchedCompanies, setWatchedCompanies] = useState([]);
  const [companyWatchListOpen, setCompanyWatchListOpen] = useState(false);  

  const activeFilterCount = [
    npi.trim() !== "",
    state !== "ALL",
    specialty !== "ALL",
  ].filter(Boolean).length;

  async function handleCompanySearch(e) {  
    e.preventDefault();
    if (!companyInput.trim()) return;
    if (!canSearch()) {
      if (!isSignedIn) {
        setShowSignInModal(true);
      } else {
        setUpgradeMessage(`You've reached your daily search limit. Upgrade to Pro for unlimited searches and full access.`);
        setShowUpgradeModal(true);
      }
      return;
    }    
    await incrementSearch();
    navigate(`/company-results?q=${encodeURIComponent(companyInput.trim())}`);    
  }

  async function selectCompanySuggestion(name) {    
    if (!canSearch()) {
      if (!isSignedIn) {
        setShowSignInModal(true);
      } else {
        setUpgradeMessage(`You've reached your daily search limit. Upgrade to Pro for unlimited searches and full access.`);
        setShowUpgradeModal(true);
      }
      return;
    }    
    await incrementSearch();
    setCompanyInput(titleCaseCompany(name));
    setShowCompanySuggestions(false);
    navigate(`/company-results?q=${encodeURIComponent(name)}`);    
  }  

  async function handleSearch(e) {    
    e.preventDefault();
    if (!lastName.trim() && !npi.trim()) return;

    if (!canSearch()) {
      if (!isSignedIn) {
        setShowSignInModal(true);
      } else {
        setUpgradeMessage(`You've reached your daily search limit. Upgrade to Pro for unlimited searches and full access.`);
        setShowUpgradeModal(true);
      }
      return;
    }    

    await incrementSearch();

    const params = new URLSearchParams();
    if (firstName) params.set("first", firstName);
    if (lastName) params.set("last", lastName);
    if (npi) params.set("npi", npi);
    if (state !== "ALL") params.set("state", state);
    if (specialty !== "ALL") params.set("specialty", specialty);

    navigate(`/results?${params.toString()}`);
  }

useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedProviders(user.id)
      .then((data) => setWatchedProviders(data))
      .catch(() => {});
    getWatchedCompanies(user.id)
      .then((data) => setWatchedCompanies(data))
      .catch(() => {});
  }, [isSignedIn, user]);  

useEffect(() => {
    if (companyInput.trim().length < 2) {
      setCompanySuggestions([]);
      return;
    }
    const q = companyInput.toUpperCase();
    const matches = KNOWN_COMPANIES.filter((c) => c.includes(q));
    setCompanySuggestions(matches);
  }, [companyInput]);  

  return (
    <main className="max-w-3xl mx-auto px-4 py-16">
      {/* Hero */}
      <div className="text-center mb-12">
      {/*  <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/20 text-gold-400 text-xs px-3 py-1 rounded-full mb-6">
          <span>◎</span>
          <span>CMS Open Payments Database</span>
        </div> */}
        <h1 className="font-display text-5xl sm:text-6xl text-white mb-4 leading-tight">
          Follow the money<br />
          <span className="text-gold-400">in medicine.</span>
        </h1>
        <p className="text-white/50 text-lg max-w-md mx-auto leading-relaxed">
          Search federal records to see exclusions or financial relationships between providers
          and pharmaceutical or medical device companies.
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className="bg-navy-800 border border-white/10 rounded-2xl p-6 mb-6">

        {/* Name fields */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <label className="text-white/40 text-xs mb-1.5 block">Provider First name</label>
            <input
              className="input-base"
              placeholder="e.g. Sarah"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={npi.trim() !== ""}
            />
          </div>
          <div className="flex-1">
            <label className="text-white/40 text-xs mb-1.5 block">
              Last name {!npi.trim() && <span className="text-gold-500/60">*</span>}
            </label>
            <input
              className="input-base"
              placeholder="e.g. Chen"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={npi.trim() !== ""}
            />
          </div>
        </div>

        {/* Advanced filters toggle */}
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors mb-4"
        >
          <span>⊞</span>
          <span>Advanced filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-gold-500 text-navy-900 text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <span className="text-white/20">{advancedOpen ? "▲" : "▼"}</span>
        </button>

        {advancedOpen && (
          <div className="space-y-4 mb-4 pt-4 border-t border-white/10">

            <div className="grid grid-cols-2 gap-3">
              {/* State */}
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">State</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gold-500/60"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s === "ALL" ? "All states" : s}</option>
                  ))}
                </select>
              </div>

              {/* Specialty */}
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">Specialty</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gold-500/60"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                >
                  {SPECIALTIES.map((s) => (
                    <option key={s} value={s}>{specialtyLabel(s)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* NPI */}
            <div>
              <label className="text-white/40 text-xs mb-1.5 block">
                Search by NPI number instead of name
              </label>
              <input
                className="input-base"
                placeholder="e.g. 1234567890"
                value={npi}
                maxLength={10}
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, ""))}
              />
              {npi.trim() !== "" && (
                <p className="text-white/30 text-xs mt-1">
                  Name fields are disabled when searching by NPI
                </p>
              )}
            </div>
            
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() => { setNpi(""); setState("ALL"); setSpecialty("ALL"); }}
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                ✕ Clear all filters
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!lastName.trim() && !npi.trim()}
        >
          Search Open Payments →
        </button>

        {!isPro && (
          <p className="text-center text-white/30 text-xs mt-3">
            {searchesRemaining > 0
              ? `${searchesRemaining} free search${searchesRemaining !== 1 ? "es" : ""} remaining`
              : `Free limit reached — upgrade for unlimited searches`}
          </p>
        )}
      </form>

{/* Divider + Company Search toggle */}
      <div className="flex items-center gap-4 my-2">
        <div className="flex-1 border-t border-white/10" />
        <button
          type="button"
          onClick={() => setCompanySearchOpen(!companySearchOpen)}
          className="flex items-center gap-2 text-white/50 hover:text-white/80 text-sm font-medium transition-colors px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20 bg-navy-800"
        >
          <span>◈</span>
          <span>Search by Company</span>
          <span className="text-white/30">{companySearchOpen ? "▲" : "▼"}</span>
        </button>
        <div className="flex-1 border-t border-white/10" />
      </div>

      {/* Company search form — collapsible */}
      {companySearchOpen && (
        <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 mb-6">

          {/* Company search bar with suggestions */}
          <form onSubmit={handleCompanySearch} className="relative mb-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  className="input-base w-full"
                  placeholder="e.g. Pfizer, Medtronic, AstraZeneca..."
                  value={companyInput}
                  onChange={(e) => {
                    setCompanyInput(e.target.value);
                    setShowCompanySuggestions(true);
                  }}
                  onFocus={() => setShowCompanySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 150)}
                  autoComplete="off"
                />
                {/* Suggestions dropdown */}
                {showCompanySuggestions && companySuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-navy-800 border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                    {companySuggestions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onMouseDown={() => selectCompanySuggestion(name)}
                        className="w-full text-left px-4 py-3 text-sm text-white/70 hover:bg-navy-700 hover:text-white transition-colors border-b border-white/5 last:border-0"
                      >
                        {titleCaseCompany(name)}
                      </button>
                    ))}
                    <div className="px-4 py-2 text-white/20 text-xs border-t border-white/5">
                      Don't see your company? Type the full name and press Search.
                    </div>
                  </div>
                )}
              </div>
              <button type="submit" className="btn-primary px-5 shrink-0">
                Search →
              </button>
            </div>
            {!isPro && (
              <p className="text-center text-white/30 text-xs mt-3">
                {searchesRemaining > 0
                  ? `${searchesRemaining} free search${searchesRemaining !== 1 ? "es" : ""} remaining`
                  : `Free limit reached — upgrade for unlimited searches`}
              </p>
            )}
          </form>

          {/* Popular companies */}
          <div>
            <p className="text-white/30 text-xs mb-2">Popular searches:</p>                        
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Abbott", value: "Abbott Laboratories" },
                { label: "AbbVie", value: "Abbvie Inc." },
                { label: "Boston Scientific", value: "Boston Scientific Corporation" },
                { label: "Eli Lilly", value: "Eli Lilly And Company" },
                { label: "Medtronic", value: "Medtronic, Inc." },
                { label: "Moderna", value: "ModernaTX, Inc." },
                { label: "Novo Nordisk", value: "Novo Nordisk Inc" },
                { label: "Pfizer", value: "Pfizer Inc." },
                { label: "Stryker", value: "Stryker Corporation" },
              ].map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => selectCompanySuggestion(value.toUpperCase())}
                  className="text-xs bg-navy-700 border border-white/10 hover:border-gold-500/30 text-white/50 hover:text-white/80 px-3 py-1.5 rounded-lg transition-all"
                >
                  {label}
                </button>
              ))}              
            </div>
          </div>
        </div>
      )}      

      {/* How it works */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
        {[
          { icon: "⌕", title: "Search a physician", body: "Enter a provider's name, NPI number, state, or specialty to pull their federal payment records." },
          { icon: "◈", title: "See the relationships", body: "View every company that paid them, and how much." },
          { icon: "✦", title: "Explore the products", body: "See which drugs or devices are associated with each payment." },
        ].map((item) => (
          <div key={item.title} className="card text-center">
            <div className="text-gold-400 text-2xl mb-3">{item.icon}</div>
            <div className="text-white font-medium text-sm mb-1">{item.title}</div>
            <div className="text-white/40 text-xs leading-relaxed">{item.body}</div>
          </div>
        ))}
      </div>

{/* Currently Watching Section */}
      {isSignedIn && isPro && (
        <div className="mt-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-white/40 text-xs uppercase tracking-widest font-medium">Currently Watching</span>
            <div className="flex-1 border-t border-white/10" />
          </div>
        <div className="card">          
          <button
            onClick={() => setWatchListOpen(!watchListOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">🔔 Provider Watch List</span>
              {watchedProviders.length > 0 && (
                <span className="bg-gold-500/20 border border-gold-500/30 text-gold-400 text-xs px-2 py-0.5 rounded-full">
                  {watchedProviders.length}
                </span>
              )}
            </div>
            <span className="text-white/30 text-xs">{watchListOpen ? "▲" : "▼"}</span>
          </button>

          {watchListOpen && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
              {watchedProviders.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">
                  You aren't watching any providers yet. Search for a provider and click "🔕 Watch" to add them.
                </p>
              ) : (
                watchedProviders.map((p) => (
                  <div key={p.npi} className="bg-navy-700/50 border border-white/5 rounded-lg px-3 py-2">
                    {/* Row 1: provider name + unwatch button */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Link
                        to={`/physician/${p.npi}?name=${encodeURIComponent(p.provider_name)}`}
                        className="text-white/80 text-sm hover:text-gold-300 transition-colors truncate"
                      >
                        {p.provider_name}
                      </Link>
                      <button
                        onClick={async () => {
                          await unwatchProvider(user.id, p.npi);
                          setWatchedProviders((prev) => prev.filter((w) => w.npi !== p.npi));
                        }}
                        className="text-white/20 hover:text-red-400 text-xs transition-colors shrink-0"
                      >
                        ✕
                      </button>
                    </div>
                    {/* Row 2: NPI + Compare button */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-white/30 text-xs font-mono">NPI: {p.npi}</p>
                      <CompareButton provider={{
                        npi: p.npi,
                        firstName: (p.provider_name || "").split(" ")[0] || "",
                        lastName: (p.provider_name || "").split(" ").slice(1).join(" ") || "",
                        state: p.state || "",
                        totalPayments: p.total_payments || 0,
                      }} />
                    </div>
                    {/* Row 3: data badges */}
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                      {p.has_payments && (
                        <span className="text-white/30 text-xs">💊 Payments</span>
                      )}
                      {p.has_ownership && (
                        <span className="text-white/30 text-xs">🏦 Ownership</span>
                      )}
                      {p.has_research && (
                        <span className="text-white/30 text-xs">🔬 Research</span>
                      )}
                      {p.is_excluded && (
                        <span className="text-red-400 text-xs">⚠ Excluded</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
</div>

        {/* Company Watch List */}
        <div className="card mt-4">          
          <button
            onClick={() => setCompanyWatchListOpen(!companyWatchListOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">🏢 Company Watch List</span>
              {watchedCompanies.length > 0 && (
                <span className="bg-gold-500/20 border border-gold-500/30 text-gold-400 text-xs px-2 py-0.5 rounded-full">
                  {watchedCompanies.length}
                </span>
              )}
            </div>
            <span className="text-white/30 text-xs">{companyWatchListOpen ? "▲" : "▼"}</span>
          </button>
          {companyWatchListOpen && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
              {watchedCompanies.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">
                  You aren't watching any companies yet. Visit a company page and click "🔕 Watch" to add them.
                </p>
              ) : (
                watchedCompanies.map((c) => (
                  <div key={c.company_name} className="bg-navy-700/50 border border-white/5 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <Link
                        to={c.company_id
                          ? `/company/${encodeURIComponent(c.company_id)}`
                          : `/company-results?q=${encodeURIComponent(c.company_name)}`}
                        className="text-white/80 text-sm hover:text-gold-300 transition-colors truncate"
                      >
                        {titleCaseCompany(c.company_name)}
                      </Link>
                      <button
                        onClick={async () => {
                          await unwatchCompany(user.id, c.company_name);
                          setWatchedCompanies((prev) => prev.filter((w) => w.company_name !== c.company_name));
                        }}
                        className="text-white/20 hover:text-red-400 text-xs transition-colors shrink-0 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                    {c.company_id && (
                      <CompanyCompareButton companyId={c.company_id} companyName={c.company_name} />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        </div>
      )}

      <p className="text-center text-white/20 text-xs mt-10 leading-relaxed">      
        Data sourced from the CMS Open Payments database. Disclosure of a payment does not imply wrongdoing.<br />
        Payment data is updated annually by CMS. Federal exclusion data sourced from the HHS OIG List of Excluded Individuals/Entities (LEIE) and SAM.gov, updated daily.<br />
        Exclusion records are for informational purposes only — always verify directly with the source.
      </p>      
    </main>
  );
}